/**
 * ProjectLayout — wrapper for all project-scoped pages.
 *
 * Fetches the project's current stage and renders the persistent
 * ProjectStageNav above the page content. Keeps page components clean.
 */
import { type ReactNode, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ProjectStageNav } from './ProjectStageNav'
import { engineApiClient } from '@/lib/apiClient'
import type { ProjectStage } from '@/lib/contracts'

interface Props {
  /** The stage this page represents — used to highlight the active tab */
  stage: ProjectStage
  children: ReactNode
}

export function ProjectLayout({ stage, children }: Props) {
  const { id } = useParams<{ id: string }>()
  const [projectTitle, setProjectTitle] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (!id) return
    // Fetch project to get title — best effort, silent on failure
    engineApiClient.fetchProject(id).then((p) => setProjectTitle(p.title)).catch(() => {})
  }, [id])

  return (
    <div className="flex flex-col min-h-screen bg-[#02182B]">
      <ProjectStageNav currentStage={stage} projectTitle={projectTitle} />
      <div className="flex-1">
        {children}
      </div>
    </div>
  )
}
